/**
 * One data-risk trigger's tile.
 *
 * ── Every number on it is "what this page read", and it says so ─────────────
 *
 * `GET /alerts` is **cursor**-paginated and its envelope carries no total at all. A
 * tile printing a bare count would be asserting a company-wide figure the server
 * never sent — the same class of mistake as rendering a withheld value as zero, one
 * level up. So the label is *"đã đọc n"*, and a tile with nothing on it says **"no
 * alerts among what was read"** rather than "0 sự cố": the second is a claim about
 * the company and the first is a claim about the request.
 *
 * ── The severity is the worst one read, icon + text ─────────────────────────
 *
 * `worst: null` means no rows were read, and it renders as the absence rather than as
 * `informational` — the quietest possible badge over "we do not know" is exactly the
 * reassuring-when-wrong direction this build has a rule about.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { useI18n } from '../../i18n';
import type { MessageKey } from '../../i18n/messages';
import type { AlertTrigger } from '../../api/types';
import { BADGE_SEVERITY, type TriggerCensus } from '../../alerts/inbox';
import { SEVERITY_LABEL } from '../../incidents/labels';
import { Badge } from '../../states/Badge';
import { SEVERITY_ICON } from '../../states/icons';

const TRIGGER_LABEL: Readonly<Record<AlertTrigger, MessageKey>> = {
  extended_idle: 'trigger.extendedIdle',
  no_active_task: 'trigger.noActiveTask',
  estimate_exceeded: 'trigger.estimateExceeded',
  productivity_drop: 'trigger.productivityDrop',
  workload_overload: 'trigger.workloadOverload',
  workload_underload: 'trigger.workloadUnderload',
  late_arrival: 'trigger.lateArrival',
  early_leave: 'trigger.earlyLeave',
  missing_hours: 'trigger.missingHours',
  agent_offline: 'trigger.agentOffline',
  agent_stopped: 'trigger.agentStopped',
  agent_uninstalled: 'trigger.agentUninstalled',
  unknown_device: 'trigger.unknownDevice',
  usb_unapproved: 'trigger.usbUnapproved',
  bulk_file_operation: 'trigger.bulkFileOperation',
  blocked_app: 'trigger.blockedApp',
  blocked_website: 'trigger.blockedWebsite',
  after_hours_activity: 'trigger.afterHoursActivity',
};

export interface TriggerTileProps {
  readonly census: TriggerCensus;
}

export function TriggerTile({ census }: TriggerTileProps): ReactNode {
  const { t } = useI18n();
  const label = t(TRIGGER_LABEL[census.trigger]);
  return (
    <li className="wp-c-figure" data-testid={`l5-tile-${census.trigger}`}>
      <p className="wp-label">{label}</p>
      {census.worst === null ? (
        // A sentence, at reading size, where the number would be. Set at 26px it
        // would read as a headline, and it is not one — it is the tile saying it has
        // no figure to print, which is a different claim from printing a zero.
        <p
          className="wp-c-figure__value wp-c-figure__value--words"
          data-testid={`l5-tile-none-${census.trigger}`}
        >
          {t('dataRisk.tile.noneRead')}
        </p>
      ) : (
        <>
          <p>
            <Badge
              severity={BADGE_SEVERITY[census.worst]}
              icon={SEVERITY_ICON[BADGE_SEVERITY[census.worst]]}
              label={t(SEVERITY_LABEL[census.worst])}
              testId={`l5-tile-worst-${census.trigger}`}
            />
          </p>
          {/* The reading, with its own words around it rather than as a bare digit
              under a heading. Title size and not Display, because DESIGN.md §6 gives
              26px to numbers only and this is a sentence that contains one — the
              same ruling the SLA stamp took. */}
          <p
            className="wp-c-figure__value wp-c-figure__value--sentence"
            data-testid={`l5-tile-open-${census.trigger}`}
          >
            {t('dataRisk.tile.unacknowledged', { count: census.unacknowledged })}
          </p>
          {/* The figure's provenance, printed beside it: how much this page read, so
              the count above is never mistaken for a company total. */}
          <p className="wp-c-figure__note" data-testid={`l5-tile-read-${census.trigger}`}>
            {t('dataRisk.tile.read', { count: census.read })}
          </p>
        </>
      )}
      <p className="wp-c-figure__note">
        <Link className="wp-linklike" to="/alerts" data-testid={`l5-tile-link-${census.trigger}`}>
          {t('dataRisk.tile.link', { label })}
        </Link>
      </p>
    </li>
  );
}
